import React, { useLayoutEffect, useRef } from "react";
import { NavigationType, useLocation, useNavigationType } from "react-router-dom";

interface ScheduledScrollReset {
  animationFrameIds: number[];
  timeoutIds: number[];
}

const scrollDocumentToTop = (): void => {
  window.scrollTo({ left: 0, top: 0, behavior: "auto" });
  document.documentElement.scrollTop = 0;
  document.documentElement.scrollLeft = 0;
  document.body.scrollTop = 0;
  document.body.scrollLeft = 0;
};

const cancelScheduledScrollReset = (scheduled: ScheduledScrollReset | null): void => {
  if (!scheduled) {
    return;
  }

  scheduled.animationFrameIds.forEach((id) => window.cancelAnimationFrame(id));
  scheduled.timeoutIds.forEach((id) => window.clearTimeout(id));
};

const scheduleScrollReset = (): ScheduledScrollReset => {
  const scheduled: ScheduledScrollReset = {
    animationFrameIds: [],
    timeoutIds: [],
  };

  scrollDocumentToTop();

  const firstFrameId = window.requestAnimationFrame(() => {
    scrollDocumentToTop();
    const secondFrameId = window.requestAnimationFrame(scrollDocumentToTop);
    scheduled.animationFrameIds.push(secondFrameId);
  });
  scheduled.animationFrameIds.push(firstFrameId);

  [0, 80, 240].forEach((delay) => {
    const timeoutId = window.setTimeout(scrollDocumentToTop, delay);
    scheduled.timeoutIds.push(timeoutId);
  });

  return scheduled;
};

/**
 * Resets document scroll on forward navigations while preserving browser back
 * restoration and hash-only in-page transitions such as mobile drawer history.
 */
export const ScrollToTop: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const lastLocationRef = useRef<{ pathname: string; search: string } | null>(null);
  const scheduledResetRef = useRef<ScheduledScrollReset | null>(null);

  useLayoutEffect(() => {
    if (!("scrollRestoration" in window.history)) {
      return undefined;
    }

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useLayoutEffect(() => {
    const nextLocation = { pathname, search };
    const lastLocation = lastLocationRef.current;
    lastLocationRef.current = nextLocation;

    if (lastLocation?.pathname === pathname && lastLocation.search === search) {
      return;
    }

    if (navigationType === NavigationType.Pop) {
      return;
    }

    cancelScheduledScrollReset(scheduledResetRef.current);
    scheduledResetRef.current = scheduleScrollReset();

    return () => {
      cancelScheduledScrollReset(scheduledResetRef.current);
      scheduledResetRef.current = null;
    };
  }, [navigationType, pathname, search]);

  return (<>{children}</>);
}